/**
 * Data Hub — Pipeline Reports.
 *
 * Opened from a Pipeline Run's Reports button (run-scoped). Shows the QC File
 * report for that import: coverage summary + row findings. Export uses the
 * authenticated API client — bare window.open cannot send the Bearer token.
 */
import { useEffect, useMemo, useState } from "react";

import { Download, Loader2 } from "lucide-react";

import Button from "../../Components/Button";
import Table from "../../Components/Table";
import { useAppDispatch } from "../../Store/Hooks";
import {
  downloadDataHubReportCsv,
  fetchDataHubReport,
  type DataHubReportPage,
} from "../../Store/Reducers/DataHubSlice";
import { QcFileReport } from "../components/QcFileReport";
import { TabEmpty, TabError } from "../components/TabState";
import { useActiveDataset } from "../hooks/useActiveDataset";

/** Column keys are snake_case from SQL; show them as words. */
function headerLabel(column: string): string {
  return column.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function cellText(value: any): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") return value.toLocaleString();
  return String(value);
}

export type PipelineReportsTabProps = {
  /**
   * Scopes the File report to one run.
   *
   * When the caller supplies one — the QC step's report dialog does — the
   * picker is hidden, because the answer is already decided and offering to
   * change it would let the dialog describe a different run from the console
   * it was opened from.
   */
  runId?: string | null;
};

export function PipelineReportsTab({
  runId: fixedRunId = null,
}: PipelineReportsTabProps) {
  const dispatch = useAppDispatch();
  const [dataset] = useActiveDataset();
  const runId = fixedRunId;
  const [page, setPage] = useState(1);
  const [data, setData] = useState<DataHubReportPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [dataset, runId]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    (async () => {
      const result = await dispatch(
        fetchDataHubReport({ key: "qc_file", runId, page, pageSize: 50 })
      );
      if (!live) return;
      if (fetchDataHubReport.fulfilled.match(result)) {
        setData(result.payload);
      } else {
        setError(new Error(result.payload || "Could not load this report."));
      }
      setLoading(false);
    })();
    return () => {
      live = false;
    };
  }, [dispatch, page, runId, dataset]);

  const columns = useMemo(() => {
    if (!data) return [];
    return data.columns.map(c => ({
      key: c,
      title: headerLabel(c),
      render: (value: unknown) => (
        <span className="text-gray-700">{cellText(value)}</span>
      ),
    }));
  }, [data]);

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    const result = await dispatch(
      downloadDataHubReportCsv({ key: "qc_file", runId })
    );
    if (downloadDataHubReportCsv.rejected.match(result)) {
      setExportError(
        result.payload || "Could not export this report. Try again."
      );
    }
    setExporting(false);
  };

  return (
    <div className="flex min-h-0 flex-col">
      <div className="flex min-h-[3.25rem] shrink-0 items-center gap-3 border-b border-gray-200 py-2.5 pl-4 pr-12 sm:pl-6">
        <div className="flex min-w-0 flex-1 items-baseline gap-2">
          <h2 className="m-0 text-base font-semibold leading-none text-gray-900">
            KPIs Reports
          </h2>
          {data ? (
            <span className="shrink-0 text-xs leading-none tabular-nums text-gray-500">
              {data.total.toLocaleString()} rows
            </span>
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline-primary"
          className="!h-8 shrink-0 !px-3 !py-0 text-xs"
          icon={
            exporting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )
          }
          disabled={exporting || !runId}
          onClick={() => {
            void handleExport();
          }}
        >
          {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      <div className="space-y-4 p-4">
        {exportError ? (
          <p className="m-0 text-xs text-red-600" role="alert">
            {exportError}
          </p>
        ) : null}

        {/*
          Coverage sits above the File report because it answers a question the
          row list cannot: how much of the rubric this import actually filled.
          A file can have no findings at all and still cover a third of the
          metrics the org scores on, and only this says so.
        */}
        <QcFileReport
          runId={runId ?? undefined}
          infoHint="What QC found in this import, row by row."
        />

        {error ? (
          <TabError error={error} />
        ) : loading && !data ? (
          <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading report…
          </div>
        ) : !data || data.rows.length === 0 ? (
          <TabEmpty
            message={
              runId
                ? "No file-report rows for this run. Pick another run, or run a new import."
                : "This report has no rows yet."
            }
          />
        ) : (
          <section className="space-y-2.5">
            <div>
              <h3 className="m-0 text-sm font-semibold text-gray-900">
                Row details
              </h3>
              <p className="m-0 mt-0.5 text-xs text-gray-500">
                Findings for each imported row in this run.
              </p>
            </div>
            <Table
              className="[&_th]:normal-case [&_th]:tracking-normal [&_th]:text-left [&_td]:text-left"
              columns={columns}
              data={data.rows}
              rowKey={(_, i) => i}
              emptyContent={
                <p className="m-0 text-sm text-gray-500">
                  This report has no rows yet.
                </p>
              }
            />

            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              <p className="m-0 text-xs text-gray-500">
                Page {data.page.toLocaleString()} of{" "}
                {Math.max(1, data.pages).toLocaleString()} ·{" "}
                {data.total.toLocaleString()} rows
                {loading && " · refreshing…"}
              </p>
              <div className="flex gap-1.5">
                <Button
                  type="button"
                  variant="outline-primary"
                  className="!px-3 !py-1.5 text-xs"
                  disabled={data.page <= 1 || loading}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline-primary"
                  className="!px-3 !py-1.5 text-xs"
                  disabled={data.page >= data.pages || loading}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
